import React, { useState, useEffect } from 'react';
import DatePickerField from '../UI/DatePickerField';
import api from '../../services/api';
import Modal from '../UI/Modal';
import toast from '../../utils/toast';
import { toLocalISODate, parseLocalDate, formatDateForInput } from '../../utils/dateUtils';

const PayrollPeriodForm = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    period_name: '',
    period_start_date: '',
    period_end_date: '',
    pay_date: '',
    description: ''
  });
  const [errors, setErrors] = useState({});

  // Auto-generate period name when dates change
  useEffect(() => {
    if (formData.period_start_date && formData.period_end_date) {
      const startDate = new Date(formData.period_start_date);
      const endDate = new Date(formData.period_end_date);
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      if (startDate.getMonth() === endDate.getMonth()) {
        // Same month
        const periodName = `${monthNames[startDate.getMonth()]} ${startDate.getFullYear()}`;
        setFormData(prev => ({ ...prev, period_name: periodName }));
      } else {
        // Cross-month period
        const periodName = `${startDate.getDate()} ${monthNames[startDate.getMonth()]} - ${endDate.getDate()} ${monthNames[endDate.getMonth()]} ${endDate.getFullYear()}`;
        setFormData(prev => ({ ...prev, period_name: periodName }));
      }
    }
  }, [formData.period_start_date, formData.period_end_date]);

  // Auto-set pay date (7 days after end date - MOM requirement)
  useEffect(() => {
    if (formData.period_end_date) {
      const endDate = new Date(formData.period_end_date);
      endDate.setDate(endDate.getDate() + 7); // MOM requirement: pay_date >= period_end_date + 7 days
      const payDate = formatDateForInput(endDate);
      setFormData(prev => ({ ...prev, pay_date: payDate }));
    }
  }, [formData.period_end_date]);

  const validateForm = () => {
    const newErrors = {};

    // Period name is optional (will be auto-generated if not provided)
    if (!formData.period_start_date) {
      newErrors.period_start_date = 'Start date is required';
    }
    if (!formData.period_end_date) {
      newErrors.period_end_date = 'End date is required';
    }
    if (!formData.pay_date) {
      newErrors.pay_date = 'Pay date is required';
    }

    // Date validations
    if (formData.period_start_date && formData.period_end_date) {
      if (new Date(formData.period_start_date) >= new Date(formData.period_end_date)) {
        newErrors.period_end_date = 'End date must be after start date';
      }
    }
    
    if (formData.period_end_date && formData.pay_date) {
      if (new Date(formData.pay_date) < new Date(formData.period_end_date)) {
        newErrors.pay_date = 'Pay date must be on or after end date (MOM requirement)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Map form data to PayrollRun API format
      const payload = {
        period_start_date: formData.period_start_date,
        period_end_date: formData.period_end_date,
        pay_date: formData.pay_date,
        period_name: formData.period_name || undefined, // Optional, will be auto-generated if not provided
      };
      
      const response = await api.post('/payroll-runs/create-period/', payload);
      toast.success('Payroll period created successfully!');
      onSuccess(response.data);
      handleClose();
    } catch (error) {
      console.error('Error creating payroll period:', error);
      if (error.response?.data) {
        // Map backend field names to form field names for error display
        const mappedErrors = {};
        if (error.response.data.period_start_date) {
          mappedErrors.period_start_date = error.response.data.period_start_date;
        }
        if (error.response.data.period_end_date) {
          mappedErrors.period_end_date = error.response.data.period_end_date;
        }
        if (error.response.data.pay_date) {
          mappedErrors.pay_date = error.response.data.pay_date;
        }
        if (error.response.data.period_name) {
          mappedErrors.period_name = error.response.data.period_name;
        }
        if (error.response.data.non_field_errors) {
          mappedErrors.non_field_errors = error.response.data.non_field_errors;
        }
        setErrors(mappedErrors);
      }
      toast.error('Failed to create payroll period');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      period_name: '',
      period_start_date: '',
      period_end_date: '',
      pay_date: '',
      description: ''
    });
    setErrors({});
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="📅 New Payroll Period"
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            className="px-6 py-3 text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="payroll-period-form"
            disabled={loading}
            className="px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              '✨ Create Period'
            )}
          </button>
        </>
      }
    >
      <form id="payroll-period-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Period Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Period Name
          </label>
          <input
            type="text"
            name="period_name"
            value={formData.period_name}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 rounded-full border ${
              errors.period_name ? 'border-red-300' : 'border-gray-300'
            } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
            placeholder="e.g., January 2024"
          />
          {errors.period_name && (
            <p className="text-red-500 text-sm mt-1">{errors.period_name}</p>
          )}
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <DatePickerField
              selected={parseLocalDate(formData.period_start_date)}
              onChange={(date) => {
                const val = date ? toLocalISODate(date) : '';
                setFormData(prev => ({ ...prev, period_start_date: val }));
                if (errors.period_start_date) setErrors(prev => ({ ...prev, period_start_date: '' }));
              }}
              className={`w-full pl-12 px-4 py-3 rounded-full border ${
                errors.period_start_date ? 'border-red-300' : 'border-gray-300'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
              placeholderText="Select start date"
              required
              maxDate={parseLocalDate(formData.period_end_date)}
            />
            {errors.period_start_date && (
              <p className="text-red-500 text-sm mt-1">{errors.period_start_date}</p>
            )}
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <DatePickerField
              selected={parseLocalDate(formData.period_end_date)}
              onChange={(date) => {
                const val = date ? toLocalISODate(date) : '';
                setFormData(prev => ({ ...prev, period_end_date: val }));
                if (errors.period_end_date) setErrors(prev => ({ ...prev, period_end_date: '' }));
              }}
              className={`w-full pl-12 px-4 py-3 rounded-full border ${
                errors.period_end_date ? 'border-red-300' : 'border-gray-300'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
              placeholderText="Select end date"
              required
              minDate={parseLocalDate(formData.period_start_date)}
            />
            {errors.period_end_date && (
              <p className="text-red-500 text-sm mt-1">{errors.period_end_date}</p>
            )}
          </div>
        </div>

        {/* Pay Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pay Date
            <span className="text-gray-500 text-xs ml-2">(MOM requirement: must be on or after end date)</span>
          </label>
          <DatePickerField
            selected={parseLocalDate(formData.pay_date)}
            onChange={(date) => {
              const val = date ? toLocalISODate(date) : '';
              setFormData(prev => ({ ...prev, pay_date: val }));
              if (errors.pay_date) setErrors(prev => ({ ...prev, pay_date: '' }));
            }}
            className={`w-full pl-12 px-4 py-3 rounded-full border ${
              errors.pay_date ? 'border-red-300' : 'border-gray-300'
            } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
            placeholderText="Select pay date"
            required
            minDate={parseLocalDate(formData.period_end_date)}
          />
          {errors.pay_date && (
            <p className="text-red-500 text-sm mt-1">{errors.pay_date}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description (Optional)
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-4 py-3 rounded-2xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            placeholder="Additional notes about this payroll period..."
          />
        </div>
      </form>
    </Modal>
  );
};

export default PayrollPeriodForm; 